import React, { useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../../shared/api/firebase/config';
import { usePortalContext } from '../../context/PortalContext';
import { hashPin } from '../../services/MfaService';
import { enrollClientMfa } from '../../services/MfaEnrollmentRepository';

function PinInput4({ id, value, onChange, disabled }) {
  const refsArr = useRef([]);
  const focus = (i) => refsArr.current[i]?.focus();

  const handleKey = (i, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);
    const next = value.split('');
    next[i] = digit;
    const joined = next.join('').slice(0, 4);
    onChange(joined);
    if (digit && i < 3) setTimeout(() => focus(i + 1), 0);
  };

  const handleBackspace = (i, e) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) focus(i - 1);
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted) { onChange(pasted); setTimeout(() => focus(Math.min(pasted.length - 1, 3)), 0); }
    e.preventDefault();
  };

  return (
    <div className="flex gap-3 justify-center" onPaste={handlePaste}>
      {Array.from({ length: 4 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { refsArr.current[i] = el; }}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={value[i] ? '•' : ''}
          onChange={(e) => handleKey(i, e)}
          onKeyDown={(e) => handleBackspace(i, e)}
          disabled={disabled}
          autoFocus={id === 'pin' && i === 0}
          className={`w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 outline-none transition-all
            ${value[i]
              ? 'border-[#C5A059] bg-amber-50 text-[#20243B]'
              : 'border-gray-200 bg-gray-50 text-[#20243B] focus:border-[#C5A059] focus:bg-white'
            } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        />
      ))}
    </div>
  );
}

export default function MfaEnrollment({ onEnrolled }) {
  const { crmClient } = usePortalContext();
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState('idle');

  const mismatch = pin.length === 4 && confirm.length > 0 && confirm.length === 4 && pin !== confirm;
  const showMismatch = pin.length === 4 && confirm.length === 4 && pin !== confirm;
  const ready = pin.length === 4 && confirm.length === 4 && pin === confirm && status !== 'saving' && status !== 'success';
  const isBusy = status === 'saving' || status === 'success';

  const handleCancel = async () => {
    try { await signOut(auth); } catch {}
    try { sessionStorage.clear(); } catch {}
    window.location.href = '/';
  };

  const handleSave = async () => {
    if (!ready) return;
    setStatus('saving');
    try {
      const pinHashValue = await hashPin(pin);
      await enrollClientMfa(crmClient.id, pinHashValue);
      setStatus('success');
      setTimeout(() => onEnrolled?.(), 900);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">

        {/* ── HEADER ── */}
        <div className="bg-[#20243B] px-6 py-5 relative overflow-hidden">
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: 'repeating-linear-gradient(45deg,#C5A059 0,#C5A059 1px,transparent 0,transparent 50%)', backgroundSize: '12px 12px' }}
          />
          <div className="relative">
            <h1 className="text-base font-extrabold text-white tracking-wide leading-tight">
              🛡️ Defina seu PIN de Segurança
            </h1>
            <p className="text-xs font-semibold text-white/80 mt-1.5 leading-relaxed">
              Digite um PIN de 4 dígitos e confirme-o abaixo para ativar sua proteção.
            </p>
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">

          {/* ══ BLOCO 1 — PIN ORIGINAL ══ */}
          <div>
            <p className="text-[10px] font-extrabold text-[#20243B] uppercase tracking-widest mb-3">
              Seu PIN de 4 dígitos:
            </p>
            <PinInput4
              id="pin"
              value={pin}
              onChange={(v) => { setPin(v); if (status === 'error') setStatus('idle'); }}
              disabled={isBusy}
            />
          </div>

          {/* ══ BLOCO 2 — CONFIRMAÇÃO ══ */}
          <div>
            <p className="text-[10px] font-extrabold text-[#20243B] uppercase tracking-widest mb-3">
              Confirme seu PIN:
            </p>
            <PinInput4
              id="confirm"
              value={confirm}
              onChange={(v) => { setConfirm(v); if (status === 'error') setStatus('idle'); }}
              disabled={isBusy}
            />
            {showMismatch && (
              <p className="mt-2.5 text-xs text-red-600 font-semibold text-center flex items-center justify-center gap-1.5">
                <i className="fas fa-times-circle" />
                Os PINs não coincidem. Verifique e tente novamente.
              </p>
            )}
            {status === 'success' && (
              <p className="mt-2.5 text-xs text-green-700 font-bold text-center flex items-center justify-center gap-1.5">
                <i className="fas fa-check-circle" />
                PIN ativado com sucesso! Acessando o portal...
              </p>
            )}
            {status === 'error' && (
              <p className="mt-2.5 text-xs text-red-600 font-semibold text-center flex items-center justify-center gap-1.5">
                <i className="fas fa-exclamation-circle" />
                Erro ao salvar. Tente novamente.
              </p>
            )}
          </div>

          {/* ══ BOTÕES ══ */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isBusy}
              className="flex-1 py-3 rounded-xl font-extrabold uppercase tracking-widest text-xs transition-all border-2"
              style={{
                background: 'transparent',
                color: '#C5A059',
                borderColor: '#C5A059',
                cursor: isBusy ? 'not-allowed' : 'pointer',
                opacity: isBusy ? 0.5 : 1,
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!ready}
              className="flex-1 py-3 rounded-xl font-extrabold uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2"
              style={{
                background: ready ? '#C5A059' : '#e5e7eb',
                color: ready ? '#20243B' : '#9ca3af',
                cursor: ready ? 'pointer' : 'not-allowed',
              }}
            >
              {status === 'saving' ? (
                <>
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-[#20243B]/30 border-t-[#20243B] animate-spin" />
                  Ativando...
                </>
              ) : (
                <>
                  <i className="fas fa-lock" />
                  OK / Ativar Proteção
                </>
              )}
            </button>
          </div>

        </div>

        {/* ══ FOOTER ══ */}
        <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-100">
          <p className="text-[11px] font-semibold text-gray-600 text-center leading-relaxed">
            <strong className="text-gray-700">Guarde seu PIN em local seguro.</strong><br />
            Em caso de esquecimento, solicite redefinição ao escritório.
          </p>
        </div>

      </div>
    </div>
  );
}
